import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { TagBadge } from '@/components/shared/TagBadge';
import { t } from '@/i18n';
import type { Tag as TagType } from '@ip/shared-types';

interface BulkTagsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tags: TagType[];
  selectedTagIds: string[];
  onToggleTag: (tagId: string, checked: boolean) => void;
  count: number;
  updating: boolean;
  onSubmit: () => void;
}

export function BulkTagsDialog({
  open,
  onOpenChange,
  tags,
  selectedTagIds,
  onToggleTag,
  count,
  updating,
  onSubmit,
}: BulkTagsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('invoices.bulk.addTagsModalTitle')}</DialogTitle>
        </DialogHeader>
        <p className="text-muted-foreground text-sm">
          {t('invoices.bulk.addTagsDescription', { count })}
        </p>
        <div className="max-h-72 space-y-2 overflow-y-auto">
          {tags.map((tag) => (
            <Label key={tag.id} className="flex items-center gap-2 py-0.5 font-normal">
              <Checkbox
                checked={selectedTagIds.includes(tag.id)}
                onCheckedChange={(checked) => onToggleTag(tag.id, checked === true)}
              />
              <TagBadge name={tag.name} color={tag.color} />
            </Label>
          ))}
          {tags.length === 0 && (
            <p className="text-muted-foreground text-sm">No tags available</p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button disabled={selectedTagIds.length === 0 || updating} onClick={onSubmit}>
            {updating && <Loader2 className="size-4 animate-spin" />}
            {t('invoices.bulk.addTags')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
